import { WritableDraft } from "immer";
import assign from "lodash/assign";
import compact from "lodash/compact";
import has from "lodash/has";
import { getFilterByIdentifier } from "PFApp/components/filters_v2/helpers/get_filter_by_identifier";
import { FilterV2WithIdentifierAndUpdater } from "PFApp/components/filters_v2/stores/filters_v2_store/filters_v2_store.types";
import { areValuesEqual, hasDefaultValue, isValueEmpty } from "PFCore/helpers/filters";
import { FiltersV2 } from "PFTypes/filters_v2";

/**
 * Apply updatedFilter changes to the filter state in writable store state filters object
 *
 * @param filtersGroup - writable store state filters object
 * @param updatedFilter
 * @return Filter | undefined - returns updated filter if filter value was modified, undefined otherwise
 */
const applyFilterChanges = (
  filtersGroup: WritableDraft<FiltersV2>,
  updatedFilter: FilterV2WithIdentifierAndUpdater
) => {
  const currentFilter = getFilterByIdentifier(filtersGroup, updatedFilter);

  if (!currentFilter) {
    return;
  }

  const { value: valueOrUpdater, ...updatedFilterData } = updatedFilter;
  assign(currentFilter, updatedFilterData);

  if (!has(updatedFilter, "value")) {
    return;
  }

  const getUpdatedValue = () => {
    const updatedValue =
      typeof valueOrUpdater === "function" ? valueOrUpdater(currentFilter.value) : valueOrUpdater;

    if (currentFilter.required && isValueEmpty(updatedValue)) {
      return hasDefaultValue(currentFilter) ? currentFilter.defaultValue : currentFilter.value;
    }

    return updatedValue;
  };

  const updatedValue = getUpdatedValue();
  if (areValuesEqual(updatedValue, currentFilter.value)) {
    return;
  }

  assign(currentFilter, {
    error:
      isValueEmpty(updatedValue) || areValuesEqual(updatedValue, currentFilter.defaultValue)
        ? null
        : currentFilter.error,
    value: updatedValue
  });

  return currentFilter;
};

export const updateFilter = (
  draftState: WritableDraft<{ mainFilters: FiltersV2; childrenFilters: FiltersV2 }>,
  { isChildren, ...updatedFilter }: FilterV2WithIdentifierAndUpdater
) => applyFilterChanges(isChildren ? draftState.childrenFilters : draftState.mainFilters, updatedFilter);

export const updateFilters = (
  draftState: WritableDraft<{ mainFilters: FiltersV2; childrenFilters: FiltersV2 }>,
  filters: FilterV2WithIdentifierAndUpdater[]
) => compact(filters.map((updatedFilter) => updateFilter(draftState, updatedFilter)));
